import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const { scrollYProgress } = useScroll();
  
  // パララックス効果用のトランスフォーム
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const cardY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen bg-black overflow-hidden">
      {/* Fluid Ambient Glow - 流体的な環境光 */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        {/* シアンオーブ */}
        <motion.div
          className="absolute w-[500px] h-[500px] rounded-full blur-[120px] opacity-40"
          style={{
            background: 'radial-gradient(circle, #22d3ee 0%, transparent 70%)',
          }}
          animate={{
            x: ['-10%', '30%', '-10%'],
            y: ['20%', '60%', '20%'],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* アンバーオーブ */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-30"
          style={{
            background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)',
            right: 0,
          }}
          animate={{
            x: ['10%', '-20%', '10%'],
            y: ['30%', '70%', '30%'],
            scale: [1.1, 0.9, 1.1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* ピンクオーブ */}
        <motion.div
          className="absolute w-[450px] h-[450px] rounded-full blur-[120px] opacity-35"
          style={{
            background: 'radial-gradient(circle, #ff006e 0%, transparent 70%)',
            left: '50%',
            top: '50%',
          }}
          animate={{
            x: ['-50%', '0%', '-50%'],
            y: ['-50%', '0%', '-50%'],
            scale: [0.9, 1.3, 0.9],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* グリーンオーブ */}
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full blur-[120px] opacity-25"
          style={{
            background: 'radial-gradient(circle, #00ff41 0%, transparent 70%)',
            bottom: '10%',
            left: '20%',
          }}
          animate={{
            x: ['0%', '40%', '0%'],
            y: ['0%', '-30%', '0%'],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </motion.div>

      {/* Grid Background */}
      <div className="absolute inset-0 opacity-10 z-[1]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <a href="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/d8e1192effeb065d9828bb3d391cea6f/487e2600aaa2d450ac57cbe9e20dd89d.png"
              alt="DigDim Logo"
              className="w-7 h-7"
            />
            <span
              className="font-display text-lg text-white tracking-wider"
              style={{ textShadow: '0 0 12px rgba(0,255,65,0.3)' }}
            >
              DigDim
            </span>
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-green/10 border border-neon-green/30">
              <div className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse" />
              <span className="text-neon-green text-[10px] font-bold tracking-wider">LIVE</span>
            </div>
            <a
              href="#makers"
              className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-neon-green hover:text-neon-green transition-all duration-300 cursor-pointer"
            >
              <i className="ri-search-line text-sm" />
            </a>
          </div>
        </div>
      </div>

      {/* Floating Light Points */}
      {[...Array(25)].map((_, i) => (
        <div
          key={i}
          className={`absolute w-1 h-1 bg-neon-green rounded-full animate-pulse z-[1] ${
            i > 14 ? 'hidden lg:block' : ''
          }`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${2 + Math.random() * 2}s`,
          }}
        />
      ))}

      <div className="container mx-auto px-4 sm:px-8 lg:px-16 py-10 lg:py-20 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between min-h-screen pt-14 lg:pt-8 pb-20 lg:pb-8 lg:gap-16">
          {/* Left Side - Cinematic Typography */}
          <motion.div
            className="w-full lg:w-2/5 relative"
            style={{ y: textY }}
          >
            {/* Mobile Marquee Ticker */}
            <div className="lg:hidden mb-6 overflow-hidden">
              <div className="flex items-center gap-3 animate-pulse">
                <div
                  className="w-2 h-2 rounded-full bg-neon-pink"
                  style={{ boxShadow: '0 0 10px #ff006e' }}
                />
                <span className="text-gray-500 text-[10px] tracking-[0.3em] uppercase">
                  Underground Music Discovery Platform
                </span>
              </div>
            </div>

            {/* Cinematic Typography - 巨大見出し */}
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl xl:text-[180px] leading-none mb-4 sm:mb-8 lg:mb-12 relative font-black">
              <span
                className="inline-block bg-gradient-to-r from-white via-cyan-300 to-amber-300 bg-clip-text text-transparent"
                style={{
                  textShadow: '3px 0 #ff006e, -3px 0 #00d9ff',
                  animation: 'glitch 3s infinite',
                }}
              >
                Dig-Dim
              </span>
            </h1>

            <p
              className="text-neon-green text-base sm:text-2xl lg:text-3xl xl:text-4xl font-light tracking-[0.3em] mb-4 sm:mb-12 lg:mb-16 font-black"
              style={{ textShadow: '0 0 20px #00ff41' }}
            >
              街の摩擦から、音が生まれる
            </p>

            {/* Glassmorphism 検索バー風装飾 */}
            <motion.div 
              className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-2xl p-6 mb-8 max-w-md"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed">
                アルゴリズムの推薦に飽きた？<br />
                自分の足で見つけた<strong className="text-white">「偶然」</strong>に価値を感じる。<br />
                街中の物体にスマホを当てて、<strong className="text-neon-pink">剥き出しの音</strong>を発見しよう。
              </p>
            </motion.div>
          </motion.div>

          {/* Right Side - NFC Card with Glassmorphism & Parallax */}
          <motion.div
            className="w-full lg:w-3/5 xl:w-1/2 relative mt-4 lg:mt-0 mb-8 lg:mb-0"
            style={{ y: cardY }}
          >
            <div className="relative animate-float-delayed">
              {/* Card Shadow */}
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-[200px] sm:w-[280px] lg:w-[450px] xl:w-[500px] h-[270px] sm:h-[380px] lg:h-[600px] xl:h-[650px] bg-neon-blue/20 blur-3xl rounded-3xl" />

              {/* Main Card with Glassmorphism */}
              <motion.div
                className="relative w-[200px] sm:w-[280px] lg:w-[450px] xl:w-[500px] h-[270px] sm:h-[380px] lg:h-[600px] xl:h-[650px] mx-auto rounded-3xl overflow-hidden backdrop-blur-xl border-2 border-white/30"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(26, 26, 26, 0.7) 0%, rgba(45, 45, 45, 0.5) 50%, rgba(26, 26, 26, 0.7) 100%)',
                  transform: 'perspective(1000px) rotateY(-15deg) rotateX(5deg)',
                  boxShadow:
                    '0 0 60px rgba(0, 217, 255, 0.4), inset 0 0 40px rgba(255, 0, 110, 0.2)',
                }}
                whileHover={{ 
                  rotateY: -10,
                  rotateX: 3,
                  scale: 1.02,
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Hologram Effect */}
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    background:
                      'linear-gradient(45deg, transparent 30%, rgba(0, 255, 65, 0.3) 50%, transparent 70%)',
                    animation: 'shimmer 3s infinite',
                  }}
                />

                {/* Circuit Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" viewBox="0 0 400 500">
                    <circle cx="50" cy="50" r="3" fill="#00ff41" />
                    <line x1="50" y1="50" x2="150" y2="100" stroke="#00ff41" strokeWidth="1" />
                    <circle cx="150" cy="100" r="3" fill="#00ff41" />
                    <line x1="150" y1="100" x2="250" y2="80" stroke="#00d9ff" strokeWidth="1" />
                    <circle cx="250" cy="80" r="3" fill="#00d9ff" />
                    <line x1="250" y1="80" x2="350" y2="120" stroke="#ff006e" strokeWidth="1" />
                    <circle cx="350" cy="120" r="3" fill="#ff006e" />
                    <line x1="100" y1="200" x2="200" y2="250" stroke="#00ff41" strokeWidth="1" />
                    <circle cx="200" cy="250" r="3" fill="#00ff41" />
                    <line x1="200" y1="250" x2="300" y2="220" stroke="#ff006e" strokeWidth="1" />
                    <circle cx="300" cy="220" r="3" fill="#ff006e" />
                  </svg>
                </div>

                {/* Card Content */}
                <div className="relative z-10 p-4 sm:p-8 lg:p-12 h-full flex flex-col justify-between">
                  <div>
                    <div className="text-neon-green text-[10px] sm:text-sm lg:text-base font-light tracking-widest mb-2 sm:mb-4 lg:mb-6">
                      NFC ENABLED
                    </div>
                    <h3 className="text-white text-lg sm:text-3xl lg:text-5xl font-bold mb-1 sm:mb-2 lg:mb-4">
                      UNDERGROUND
                    </h3>
                    <p className="text-gray-400 text-[10px] sm:text-sm lg:text-lg">Tap to Discover</p>
                  </div>

                  <div className="text-center">
                    <motion.div
                      className="w-12 h-12 sm:w-24 sm:h-24 lg:w-32 lg:h-32 mx-auto mb-2 sm:mb-6 lg:mb-8 rounded-full border-2 sm:border-4 lg:border-6 border-neon-pink flex items-center justify-center backdrop-blur-md bg-white/5"
                      style={{ boxShadow: '0 0 30px rgba(255, 0, 110, 0.6)' }}
                      animate={{
                        scale: [1, 1.1, 1],
                        boxShadow: [
                          '0 0 30px rgba(255, 0, 110, 0.6)',
                          '0 0 50px rgba(255, 0, 110, 0.8)',
                          '0 0 30px rgba(255, 0, 110, 0.6)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      <i className="ri-nfc-line text-xl sm:text-4xl lg:text-6xl text-neon-pink" />
                    </motion.div>
                    <p className="text-white text-xs sm:text-lg lg:text-2xl font-light">
                      物体に触れて<br />
                      音を解放する
                    </p>
                  </div>

                  <div className="flex justify-between items-center text-[8px] sm:text-xs lg:text-sm text-gray-500">
                    <span>DigDim PLATFORM</span>
                    <span>v1.0</span>
                  </div>
                </div>
              </motion.div>

              {/* Glassmorphism 浮遊カード装飾 */}
              <motion.div
                className="hidden lg:block absolute -left-20 top-1/4 w-32 h-40 backdrop-blur-xl bg-white/5 border border-white/20 rounded-xl p-4"
                animate={{
                  y: [0, -20, 0],
                  rotate: [-5, 5, -5],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <div className="text-cyan-400 text-xs mb-2">ARTIST</div>
                <div className="w-full h-16 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg mb-2" />
                <div className="text-white text-[10px] font-bold">Underground Sound</div>
              </motion.div>

              <motion.div
                className="hidden lg:block absolute -right-24 bottom-1/3 w-36 h-44 backdrop-blur-xl bg-white/5 border border-white/20 rounded-xl p-4"
                animate={{
                  y: [0, 20, 0],
                  rotate: [5, -5, 5],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 1,
                }}
              >
                <div className="text-amber-400 text-xs mb-2">EVENT</div>
                <div className="w-full h-20 bg-gradient-to-br from-amber-500/20 to-pink-500/20 rounded-lg mb-2" />
                <div className="text-white text-[10px] font-bold">Live Tonight</div>
              </motion.div>

              {/* Action Button */}
              <div className="mt-6 sm:mt-12 lg:mt-16 text-center">
                <motion.a
                  href="/nfc"
                  className="group relative inline-flex px-8 sm:px-12 lg:px-16 py-3 sm:py-4 lg:py-5 bg-black/50 backdrop-blur-xl border-2 border-white rounded-full text-white font-bold text-sm sm:text-lg lg:text-xl tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer no-underline"
                  whileHover={{ 
                    scale: 1.05,
                    y: -8,
                    boxShadow: '0 20px 60px rgba(255,255,255,0.4)',
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="relative z-10 flex items-center gap-2 sm:gap-3 lg:gap-4">
                    CRASH
                    <i className="ri-fire-line text-neon-pink text-lg lg:text-2xl" />
                  </span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes shimmer {
          0%, 100% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
        }
        @keyframes glitch {
          0%, 100% { text-shadow: 3px 0 #ff006e, -3px 0 #00d9ff; }
          25% { text-shadow: -3px 0 #ff006e, 3px 0 #00d9ff; }
          50% { text-shadow: 3px 0 #00d9ff, -3px 0 #ff006e; }
          75% { text-shadow: -3px 0 #00d9ff, 3px 0 #ff006e; }
        }
      `}</style>
    </section>
  );
}